// src/ui/tokens.js
//
// The ONE design-token set for the 2026 redesign. Every value below was read
// out of the approved mockups' <style> blocks and inline styles
// (C:\Crypto\design-mockups\project\*.dc.html, canvas v44). The usage count
// in the comments is how many times the hex appears across the 88 artboards,
// so a later unit can tell a system colour from a one-off.
//
// ui.css mirrors these as CSS custom properties on `.ui-root` (--ui-*). Use
// the CSS classes / components first; reach for these constants only for SVG
// fills, inline chart colours and the MUI theme.

export const color = {
  // ── Ground and surfaces ────────────────────────────────────────────────
  ground: '#0A0D13', // page background (183)
  chrome: '#0C1017', // phone tab bar, table group rows, wizard footer (95)
  surface: '#0E121A', // .card (102)
  surface2: '#10141D', // .kpi tiles, .step, .mode (152)
  raised: '#0F141D', // dialogs, toasts (12)
  input: '#121722', // .seg track, .chip, .in, .search, .box (296)
  hover: '#111722', // row hover (80)
  selected: '#131B28', // selected list row (34)
  choiceOn: '#131C2A', // selected .choice / .preset / .st (29)
  tint: '#16202F', // selected chip, icon well (85)
  segOn: '#223047', // selected segment (93)
  tag: '#172131', // .tag bg, .act bg (157)
  cond: '#141C2A', // condition chips, .pillin (65)
  optBg: '#121A26', // chooser option card (7)
  empty: '#141A26', // empty heat cell / meter track alt (48)
  neutralTag: '#1A2230', // "Can't tell yet" pill bg, step number well (107)
  iconWell: '#1A2638', // chooser icon (7)

  // ── Lines ──────────────────────────────────────────────────────────────
  line: '#1A2130', // card / kpi border (266)
  lineRow: '#161D2A', // row separators, phone card border (511)
  lineHeader: '#151B27', // header / tabs bottom border (265)
  lineInput: '#1D2432', // input / seg border (288)
  lineAct: '#243149', // .act border, dialog border (161)
  lineSel: '#2C3C57', // selected row border, dashed add (45)
  lineCheck: '#3A4558', // checkbox border, evidence zero line (77)
  lineMuted: '#2A3345', // toggle track off, empty checkbox (51)
  divider: '#232C3D', // vertical dividers (10)
  lineAuth: '#121824', // public/auth header border (18)

  // ── Text ───────────────────────────────────────────────────────────────
  text: '#E4E9F2', // body text (430)
  title: '#F4F7FB', // titles, KPI values, active nav (1021)
  strong: '#EEF2F8', // row names (.nm) (241)
  soft: '#DCE3EE', // group headings (125)
  actText: '#DCE6F3', // .act button text (98)
  body2: '#C9D3E2', // chips, secondary body (180)
  lead: '#B9C3D4', // landing / about lead paragraph (3)
  pillText: '#A9B6CB', // neutral pill text (95)
  mut: '#98A3B7', // .mut, .adv (450)
  sub: '#8C97AB', // option descriptions (12)
  label: '#7E899D', // labels, nav links, kpi captions (1269)
  faint: '#6F7A8F', // .sm, table head, hints (640)
  dim: '#5E6A82', // counts in sub tabs, paused dot (136)
  arrow: '#4B5870', // flow arrows (6)

  // ── Accent / action ────────────────────────────────────────────────────
  accent: '#8FC0EA', // links, active underline (505)
  accentHover: '#B9D8F3', // link hover (85)
  tagText: '#9CC3E4', // .tag text (70)
  primary: '#3B6FA6', // primary button, checked box, meters (347)
  chart: '#4D8FC7', // bar-chart fill (45)
  rangeBar: '#2E4E73', // evidence strip 95% bar (58)

  // ── Semantic ───────────────────────────────────────────────────────────
  up: '#4FBF94', // positive (750)
  upBg: '#16261F', // positive tag bg (250)
  upBand: '#2F6B55', // positive range / unseen-test band (12)
  down: '#E08A80', // negative (687)
  downBg: '#2A1C1A', // .sell / .gD bg (88)
  downBg2: '#2C1A18', // "No edge" pill bg (45)
  downBand: '#7A3F39', // negative range band (2)
  downLine: '#5A2E2A', // .danger border (74)
  halt: '#C45C50', // solid danger button (Halt) (6)
  flattenLine: '#5A3A2A', // Flatten border (2)
  flattenText: '#E8B08A', // Flatten text (2)
  watch: '#E2BE6A', // amber (336)
  watchBg: '#2B2413', // amber tag bg (163)
  watchBand: '#6B5A2E', // amber evidence range (10)
  changed: '#C9A24A', // changed-field border (10)
  changedBg: '#1E1A10',

  // Banners (StatesProgress .warnb)
  infoBg: '#132033', infoLine: '#27466B', infoText: '#CFE2F5',
  warnBg: '#241F12', warnLine: '#4A3D1C', warnText: '#EBD49A', warnTitle: '#F4E3B5',
  errBg: '#2A1715', errLine: '#5A2E2A', errText: '#F1C1BA', errTitle: '#F8D6D0',
  okBg: '#12211B', okLine: '#1F3A2E', okText: '#BFE5D3',

  // Categorical series (donut / allocation legends in Portfolio + Portfolio Lab)
  series: ['#8FC0EA', '#4FBF94', '#E0A04A', '#A792E0', '#5EC2B0', '#6FA6D6', '#E08A80'],

  white: '#FFFFFF',
  scrim: 'rgba(5,7,11,.72)',
};

export const font = {
  sans: '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", sans-serif',
  mono: '"IBM Plex Mono", ui-monospace, Menlo, monospace',
};

// Type scale (px). Desktop values; phone values in `typePhone` where they differ.
export const type = {
  h1: 26, // page title (Research 26, wizard 24)
  h1Wizard: 24,
  sheet: 22, // sheet / section title
  dialog: 17, // dialog h4
  empty: 16, // empty-state title
  h3: 15, // card title
  body: 14,
  row: 14,
  ui: 13.5, // buttons, tabs, links
  seg: 13,
  label: 12.5,
  small: 12,
  tiny: 11,
  kpi: 26,
  kpiPhone: 24,
  k2: 22,
  stat: 19,
  wordmark: 16,
  nav: 14,
  tabbar: 11,
};

export const radius = {
  xs: 5, // side tags
  tag: 6,
  sm: 7, // seg button, rule chips
  md: 9, // inputs, buttons (desktop)
  mdPhone: 10, // inputs, buttons (phone)
  tile: 12, // kpi tiles, modes
  card: 14,
  dialog: 16,
  pill: 999,
};

export const space = {
  pageX: 40, // desktop side padding
  pageTop: 32,
  pageXPhone: 16,
  headerH: 60,
  headerHPhone: 56,
  tabbarH: 64,
  rowH: 54,
  headRowH: 36,
  navGap: 28,
  tabsGap: 26,
  cardPadX: 20,
  cardPadY: 18,
  gridGap: 12,
};

export const layout = {
  // ≤ 720 px renders the phone shell (56 px header + 64 px bottom tab bar).
  // The phone mockups are 390 px wide. The desktop header needs ~710 px even
  // with the tightened 721–1000 px spacing (wordmark + six links + Settings),
  // so everything narrower — phones in either orientation, small tablets in
  // split view — gets the phone shell. Tablets at 768 px and up get desktop.
  phoneMax: 720,
  // Page frame (mirrors ui.css .ui-root): gutter and content width are fluid
  // with ~20 px more side margin than the 1440 artboards (54 px gutter, 1240 px content).
  gutter: 'clamp(24px, 3.75vw, 56px)',
  contentMax: 'clamp(1240px, calc(1240px + (100vw - 1440px) / 2), 1520px)',
};

const tokens = { color, font, type, radius, space, layout };
export default tokens;
